import React, { useMemo, useState } from "react";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FaBed, FaFileExcel, FaSync, FaCheckCircle, FaSearch, FaPlus, FaEdit } from "react-icons/fa";
import homologacionCamasLogiflexApi from "../../../services/api/homologacionCamasLogiflexApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";

const ESTADOS_HOMOLOGACION = [
  { value: "", label: "Todos" },
  { value: "HOMOLOGADA", label: "Homologadas" },
  { value: "SIN_HOMOLOGAR", label: "Sin homologar (SGC)" },
  { value: "CONFLICTO", label: "Conflictos" },
  { value: "SOLO_PROVEEDOR", label: "Solo en Logiflex" },
];

const ESTADOS_SYNC = [
  { value: "", label: "Todos" },
  { value: "CREAR", label: "Crear en Logiflex" },
  { value: "ACTUALIZAR", label: "Actualizar en Logiflex" },
  { value: "YA_ALINEADA", label: "Ya alineadas" },
  { value: "SIN_HABITACION", label: "Sin habitación Logiflex" },
  { value: "UUID_YA_ASIGNADO", label: "UUID ya asignado" },
  { value: "OMITIR", label: "Omitidas" },
];

const ESTADOS_VALIDACION = [
  { value: "", label: "Todos" },
  { value: "VALIDO", label: "Válidos" },
  { value: "INCONSISTENTE", label: "Inconsistentes" },
  { value: "UUID_NO_EN_LOGIFLEX", label: "UUID no en Logiflex" },
  { value: "PLACEHOLDER", label: "Placeholders" },
  { value: "CODIGO_DESACTUALIZADO", label: "Desalineadas (actualizar Logiflex)" },
];

const badgeVariant = (estado) => {
  switch (estado) {
    case "HOMOLOGADA":
    case "VALIDO":
    case "CREAR":
    case "ACTUALIZAR":
    case "YA_ALINEADA":
      return "success";
    case "SIN_HABITACION":
    case "OMITIR":
    case "SIN_HOMOLOGAR":
    case "CODIGO_DESACTUALIZADO":
    case "PLACEHOLDER":
      return "warning";
    case "CONFLICTO":
    case "INCONSISTENTE":
    case "UUID_NO_EN_LOGIFLEX":
    case "UUID_YA_ASIGNADO":
      return "danger";
    case "SOLO_PROVEEDOR":
      return "info";
    default:
      return "secondary";
  }
};

const HomologacionCamasLogiflex = () => {
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");

  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [validando, setValidando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [syncUnaId, setSyncUnaId] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [hotel, setHotel] = useState("CAMPAMENTO");
  const [opcionCrear, setOpcionCrear] = useState(true);
  const [opcionActualizar, setOpcionActualizar] = useState(true);
  const [seleccionados, setSeleccionados] = useState([]);

  const esValidacion = resultado?.modo === "validacion";
  const esSync = resultado?.modo === "sincronizacion";
  const estadosFiltro = esValidacion
    ? ESTADOS_VALIDACION
    : esSync
    ? ESTADOS_SYNC
    : ESTADOS_HOMOLOGACION;

  const filasFiltradas = useMemo(() => {
    const filas = resultado?.filas || [];
    return filas.filter((f) => {
      if (filtroEstado && f.estado !== filtroEstado && f.accion !== filtroEstado) return false;
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      const texto = [
        f.zona,
        f.modulo,
        f.habitacion,
        f.cama,
        f.codigo_sgc,
        f.accion,
        f.codigo_proveedor,
        f.codigo_logiflex,
        f.id_proveedor,
        f.motivo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return texto.includes(q);
    });
  }, [resultado, filtroEstado, busqueda]);

  const habitacionesFaltantes = useMemo(() => {
    if (!esSync) return [];
    const map = {};
    for (const f of resultado?.filas || []) {
      if (f.accion !== "SIN_HABITACION") continue;
      const key = [f.zona, f.modulo, f.habitacion, f.habitacion_logiflex]
        .map((x) => String(x || "").trim().toUpperCase())
        .join("|");
      if (!map[key]) {
        map[key] = {
          zona: f.zona || "",
          modulo: f.modulo || "",
          habitacion: f.habitacion || "",
          nombre_logiflex: f.habitacion_logiflex || "",
          camas: [],
        };
      }
      if (f.cama_id) map[key].camas.push(f.cama_id);
    }
    return Object.values(map).sort((a, b) =>
      `${a.zona} ${a.modulo} ${a.habitacion}`.localeCompare(
        `${b.zona} ${b.modulo} ${b.habitacion}`
      )
    );
  }, [esSync, resultado]);

  const idsSeleccionables = useMemo(
    () =>
      filasFiltradas
        .filter(
          (f) =>
            f.cama_id &&
            ["CREAR", "ACTUALIZAR", "YA_ALINEADA", "SIN_HABITACION"].includes(f.accion)
        )
        .map((f) => f.cama_id),
    [filasFiltradas]
  );

  const todosSeleccionados =
    idsSeleccionables.length > 0 &&
    idsSeleccionables.every((id) => seleccionados.includes(id));

  const toggleFila = (camaId) => {
    setSeleccionados((prev) =>
      prev.includes(camaId) ? prev.filter((id) => id !== camaId) : [...prev, camaId]
    );
  };

  const toggleTodosVisibles = () => {
    if (todosSeleccionados) {
      setSeleccionados((prev) => prev.filter((id) => !idsSeleccionables.includes(id)));
    } else {
      setSeleccionados((prev) => Array.from(new Set([...prev, ...idsSeleccionables])));
    }
  };

  const ejecutar = async (aplicar) => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }

    if (aplicar) {
      const ok = window.confirm(
        "Se actualizarán id_proveedor y codigo_proveedor en las camas homologadas. ¿Continuar?"
      );
      if (!ok) return;
      setAplicando(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await homologacionCamasLogiflexApi.ejecutar(selectedProject.id, {
        aplicar,
        hotel: hotel || "CAMPAMENTO",
        limit: 5000,
      });
      setFiltroEstado("");
      setSeleccionados([]);
      setResultado(response.data);
      toast.success(response.data?.message || "Proceso completado");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || error.message || "Error en homologación"
      );
    } finally {
      setLoading(false);
      setAplicando(false);
    }
  };

  const validarHomologadas = async () => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }

    setValidando(true);
    try {
      const response = await homologacionCamasLogiflexApi.validarHomologadas(
        selectedProject.id,
        {
          hotel: hotel || "CAMPAMENTO",
          limit: 5000,
        }
      );
      setFiltroEstado("");
      setSeleccionados([]);
      setResultado(response.data);
      toast.success(response.data?.message || "Validación completada");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || error.message || "Error al validar camas homologadas"
      );
    } finally {
      setValidando(false);
    }
  };

  const sincronizarMasiva = async (aplicar, overrides = {}) => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }
    const crear = overrides.crear ?? opcionCrear;
    const actualizar = overrides.actualizar ?? opcionActualizar;
    const camaIds = overrides.cama_ids || null;
    if (!crear && !actualizar) {
      toast.warning("Seleccione al menos una opción: crear o actualizar");
      return;
    }
    if (camaIds && !camaIds.length) {
      toast.warning("Seleccione al menos una cama en la tabla");
      return;
    }
    if (aplicar) {
      const partes = [];
      if (crear) partes.push("CREAR");
      if (actualizar) partes.push("ACTUALIZAR");
      const alcance = camaIds
        ? `${camaIds.length} cama(s) seleccionada(s)`
        : "todas las camas del proyecto";
      const ok = window.confirm(
        `Se enviará a Logiflex (${partes.join(" y ")}) ${alcance}. Se guardará id_proveedor. ¿Continuar?`
      );
      if (!ok) return;
    }
    setSincronizando(true);
    try {
      const response = await homologacionCamasLogiflexApi.sincronizar(selectedProject.id, {
        aplicar,
        crear,
        actualizar,
        cama_ids: camaIds,
        hotel: hotel || "CAMPAMENTO",
        limit: 5000,
      });
      setFiltroEstado("");
      setSeleccionados([]);
      setResultado(response.data);
      toast.success(response.data?.message || "Sincronización completada");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || error.message || "Error al sincronizar con Logiflex"
      );
    } finally {
      setSincronizando(false);
    }
  };

  const sincronizarUna = async (camaId) => {
    if (!selectedProject?.id || !camaId) return;
    setSyncUnaId(camaId);
    try {
      const response = await homologacionCamasLogiflexApi.sincronizarUna(
        selectedProject.id,
        camaId,
        hotel || "CAMPAMENTO"
      );
      const data = response.data;
      if (data?.success) {
        toast.success(`Cama ${camaId}: ${data.accion || "OK"}`);
      } else {
        toast.error(data?.motivo || "No se pudo homologar la cama");
      }
      setResultado((prev) => {
        if (!prev?.filas) return prev;
        return {
          ...prev,
          filas: prev.filas.map((f) => (f.cama_id === camaId ? { ...f, ...data } : f)),
        };
      });
      setSeleccionados((prev) => prev.filter((id) => id !== camaId));
    } catch (error) {
      toast.error(
        error.response?.data?.detail || error.message || "Error al homologar cama"
      );
    } finally {
      setSyncUnaId(null);
    }
  };

  const aplicarSeleccion = (tipo) => {
    const filas = resultado?.filas || [];
    const ids = seleccionados.filter((id) => {
      const f = filas.find((x) => x.cama_id === id);
      if (!f) return false;
      if (tipo === "crear") {
        return f.accion === "CREAR" || f.accion === "SIN_HABITACION";
      }
      return f.accion === "ACTUALIZAR" || f.accion === "YA_ALINEADA";
    });
    if (!ids.length) {
      toast.warning(
        tipo === "crear"
          ? "Seleccione filas con acción Crear"
          : "Seleccione filas con acción Actualizar"
      );
      return;
    }
    sincronizarMasiva(true, {
      crear: tipo === "crear",
      actualizar: tipo === "actualizar",
      cama_ids: ids,
    });
  };

  const exportarExcel = () => {
    if (!filasFiltradas.length) {
      toast.warning("No hay filas para exportar");
      return;
    }

    const rows = filasFiltradas.map((f) => ({
      Estado: f.accion || f.estado,
      "ID Cama SGC": f.cama_id ?? "",
      Zona: f.zona ?? "",
      Módulo: f.modulo ?? "",
      Habitación: f.habitacion ?? "",
      Cama: f.cama ?? "",
      "Código SGC (Logiflex)": f.codigo_sgc ?? f.codigo_esperado ?? "",
      "ID Proveedor": f.id_proveedor ?? "",
      "Código Proveedor (BD)": f.codigo_proveedor ?? "",
      Perfil: f.perfil ?? "",
      "Estado operativo": f.estado_operativo ?? "",
      "Código Logiflex": f.codigo_logiflex ?? "",
      "Método match": f.metodo_match ?? "",
      Motivo: f.motivo ?? "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, esValidacion ? "Validación" : "Homologación");

    if (resultado?.resumen) {
      const wsRes = XLSX.utils.json_to_sheet([resultado.resumen]);
      XLSX.utils.book_append_sheet(wb, wsRes, "Resumen");
    }

    if (habitacionesFaltantes.length) {
      const habRows = habitacionesFaltantes.map((h) => ({
        Zona: h.zona,
        Módulo: h.modulo,
        "Habitación SGC": h.habitacion,
        "Nombre a crear en Logiflex": h.nombre_logiflex,
        "Camas SGC pendientes": h.camas.join(", "),
        Cantidad: h.camas.length,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(habRows),
        "Habitaciones a crear"
      );
    }

    const fecha = new Date().toISOString().split("T")[0];
    const prefijo = esValidacion
      ? "validacion_camas_homologadas"
      : esSync
      ? "sincronizacion_camas_logiflex"
      : "homologacion_camas_logiflex";
    XLSX.writeFile(wb, `${prefijo}_${fecha}.xlsx`);
    toast.success("Excel exportado");
  };

  const exportarHabitacionesFaltantes = () => {
    if (!habitacionesFaltantes.length) {
      toast.warning("No hay habitaciones pendientes de crear en Logiflex");
      return;
    }
    const rows = habitacionesFaltantes.map((h) => ({
      Zona: h.zona,
      Módulo: h.modulo,
      "Habitación SGC": h.habitacion,
      "Nombre a crear en Logiflex": h.nombre_logiflex,
      "Camas SGC pendientes": h.camas.join(", "),
      Cantidad: h.camas.length,
      Instrucción:
        "Crear esta habitación en Logiflex (hotel CAMPAMENTO) y luego pulsar Crear en SGC. Cada cama SGC debe obtener su propio UUID (codigo=id SGC); no reutilizar id_proveedor de otra cama.",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Habitaciones a crear");
    const fecha = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `habitaciones_crear_logiflex_${fecha}.xlsx`);
    toast.success("Excel de habitaciones faltantes exportado");
  };

  const resumen = resultado?.resumen;
  const ocupado = loading || aplicando || validando || sincronizando;

  return (
    <div className="dh-reporte-bg">
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding-bottom: 2rem;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; opacity: 0.95; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; opacity: 0.95; }
          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-info:hover { color: white; opacity: 0.95; }
          .btn-dh-warning {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-warning:hover { color: white; opacity: 0.95; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control { border-radius: 10px; }
          .dh-alert-info {
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            border: 1px solid #93c5fd;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            color: #1e40af;
          }
          .stat-box {
            text-align: center;
            padding: 1rem;
            border-radius: 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
          }
          .stat-box h4 { margin: 0; font-weight: 700; }
          .stat-box small { color: #64748b; }
          .table-homologacion { font-size: 0.875rem; }
          .table-homologacion th {
            background: #f1f5f9;
            position: sticky;
            top: 0;
            z-index: 1;
          }
        `}
      </style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaBed className="me-2" />
            Homologación de Camas — Logiflex
          </h1>
          <p className="dh-main-subtitle">
            SGC es la fuente de verdad. Logiflex se alinea con
            <code>codigo=str(cama.id)</code>, bloque (zona) y módulo.
          </p>
        </Container>
      </div>

      <Container>
        <Row className="mb-4">
          <Col>
            <div className="dh-alert dh-alert-info">
              El <strong>código</strong> enviado a Logiflex es el ID de la cama SGC.
              La clave operativa es el UUID en <code>id_proveedor</code> de esa cama.
              Homologar <strong>empuja SGC → Logiflex</strong> (código = ID SGC).
              La habitación se identifica con <code>bloque</code> (zona SGC) +
              <code>modulo</code> + número, para no cruzar p. ej. CARPA 1 y CARPA 8.
              Si crear devuelve un UUID de otra cama SGC, se valida habitación/bloque/módulo.
              Hotel: <strong>CAMPAMENTO</strong>.
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            <Card className="dh-card">
              <Card.Header className="dh-card-header dh-card-header-primary">
                Crear / actualizar camas en Logiflex
              </Card.Header>
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label className="dh-form-label">Hotel Logiflex</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={hotel}
                      onChange={(e) => setHotel(e.target.value)}
                      placeholder="CAMPAMENTO"
                    />
                  </Col>
                  <Col md={9}>
                    <Form.Label className="dh-form-label d-block">Operaciones</Form.Label>
                    <div className="d-flex flex-wrap gap-4">
                      <Form.Check
                        type="checkbox"
                        id="opcion-crear-logiflex"
                        className="dh-form-label"
                        label="Crear camas que no existen en Logiflex"
                        checked={opcionCrear}
                        onChange={(e) => setOpcionCrear(e.target.checked)}
                        disabled={ocupado}
                      />
                      <Form.Check
                        type="checkbox"
                        id="opcion-actualizar-logiflex"
                        className="dh-form-label"
                        label="Actualizar camas existentes (el código es solo el ID SGC)"
                        checked={opcionActualizar}
                        onChange={(e) => setOpcionActualizar(e.target.checked)}
                        disabled={ocupado}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="g-2 mt-3">
                  <Col className="d-flex flex-wrap gap-2">
                    <Button
                      className="btn-dh-info"
                      onClick={() => sincronizarMasiva(false)}
                      disabled={ocupado || (!opcionCrear && !opcionActualizar)}
                    >
                      {sincronizando && !aplicando ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaSync className="me-2" />
                      )}
                      Vista previa
                    </Button>
                    <Button
                      className="btn-dh-primary"
                      onClick={() =>
                        sincronizarMasiva(true, { crear: true, actualizar: false })
                      }
                      disabled={ocupado}
                    >
                      {sincronizando ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaPlus className="me-2" />
                      )}
                      Crear todas en Logiflex
                    </Button>
                    <Button
                      className="btn-dh-primary"
                      onClick={() =>
                        sincronizarMasiva(true, { crear: false, actualizar: true })
                      }
                      disabled={ocupado}
                    >
                      {sincronizando ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaEdit className="me-2" />
                      )}
                      Actualizar todas en Logiflex
                    </Button>
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => sincronizarMasiva(true)}
                      disabled={ocupado || (!opcionCrear && !opcionActualizar)}
                    >
                      {sincronizando ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaCheckCircle className="me-2" />
                      )}
                      Aplicar todas (casillas)
                    </Button>
                    <Button
                      className="btn-dh-warning"
                      onClick={validarHomologadas}
                      disabled={ocupado}
                    >
                      {validando ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaSearch className="me-2" />
                      )}
                      Validar camas homologadas
                    </Button>
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => ejecutar(false)}
                      disabled={ocupado}
                    >
                      Match legado (solo lectura)
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {resumen && (
          <Row className="mb-4 g-3">
            {(esValidacion
              ? [
                  ["Revisadas", resumen.total_revisadas],
                  ["Válidas", resumen.validas],
                  ["Inconsistentes", resumen.inconsistentes],
                  ["UUID ausente", resumen.uuid_no_en_logiflex],
                  ["Placeholders", resumen.placeholders],
                  ["Desalineadas", resumen.codigo_desactualizado],
                  ["Sin id_prov.", resumen.sin_id_proveedor],
                ]
              : esSync
              ? [
                  ["SGC", resumen.total_sgc],
                  ["Logiflex", resumen.total_proveedor],
                  ["Crear", resumen.a_crear],
                  ["Actualizar", resumen.a_actualizar],
                  ["Alineadas", resumen.ya_alineadas],
                  ["Sin hab.", resumen.sin_habitacion],
                  ["Omitidas", resumen.omitidas],
                  ["UUID duplicado", resumen.uuid_ya_asignado],
                  ["OK", resumen.ok],
                  ["Errores", resumen.errores],
                ]
              : [
                  ["SGC", resumen.total_sgc],
                  ["Logiflex", resumen.total_proveedor],
                  ["Homologadas", resumen.homologadas],
                  ["Sin homologar", resumen.sin_homologar],
                  ["Conflictos", resumen.conflictos],
                  ["Solo Logiflex", resumen.solo_proveedor],
                ]
            ).map(([label, val]) => (
              <Col key={label} xs={6} md={esValidacion ? true : 2}>
                <div className="stat-box">
                  <h4>{val ?? 0}</h4>
                  <small>{label}</small>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {esSync && habitacionesFaltantes.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>
                    Habitaciones a crear en Logiflex ({habitacionesFaltantes.length})
                  </span>
                  <Button
                    size="sm"
                    className="btn-dh-secondary"
                    onClick={exportarHabitacionesFaltantes}
                  >
                    <FaFileExcel className="me-1" /> Exportar listado
                  </Button>
                </Card.Header>
                <Card.Body>
                  <div className="dh-alert dh-alert-info mb-3">
                    Cada fila es una habitación que no se pudo crear en Logiflex
                    (token sin permiso o error del API). La creación usa
                    bloque + módulo + número; no reutiliza la cama de otro módulo.
                    Cada cama SGC necesita su propio UUID con <code>codigo=str(cama.id)</code>.
                  </div>
                  <div style={{ maxHeight: "280px", overflow: "auto" }}>
                    <Table striped bordered hover responsive className="table-homologacion mb-0">
                      <thead>
                        <tr>
                          <th>Zona</th>
                          <th>Módulo</th>
                          <th>Hab. SGC</th>
                          <th>Nombre Logiflex a crear</th>
                          <th>Camas pendientes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {habitacionesFaltantes.map((h) => (
                          <tr key={`${h.zona}-${h.modulo}-${h.habitacion}-${h.nombre_logiflex}`}>
                            <td>{h.zona}</td>
                            <td>{h.modulo}</td>
                            <td>{h.habitacion}</td>
                            <td>
                              <code>{h.nombre_logiflex || "—"}</code>
                            </td>
                            <td>
                              {h.camas.length}{" "}
                              <small className="text-muted">
                                ({h.camas.slice(0, 8).join(", ")}
                                {h.camas.length > 8 ? "…" : ""})
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {resultado && (
          <Row>
            <Col>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-success d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>
                    {esValidacion ? "Validación" : "Resultado"} ({filasFiltradas.length}{" "}
                    filas)
                    {esSync && seleccionados.length > 0
                      ? ` · ${seleccionados.length} seleccionada(s)`
                      : ""}
                  </span>
                  <div className="d-flex flex-wrap gap-2">
                    {esSync && (
                      <>
                        <Button
                          size="sm"
                          className="btn-dh-primary"
                          onClick={() => aplicarSeleccion("crear")}
                          disabled={ocupado || !seleccionados.length}
                        >
                          <FaPlus className="me-1" />
                          Crear seleccionadas
                        </Button>
                        <Button
                          size="sm"
                          className="btn-dh-primary"
                          onClick={() => aplicarSeleccion("actualizar")}
                          disabled={ocupado || !seleccionados.length}
                        >
                          <FaEdit className="me-1" />
                          Actualizar seleccionadas
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      className="btn-dh-secondary"
                      onClick={exportarExcel}
                      disabled={!filasFiltradas.length}
                    >
                      <FaFileExcel className="me-1" /> Exportar Excel
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <Form.Label className="dh-form-label">Filtrar por estado</Form.Label>
                      <Form.Select
                        className="dh-form-control"
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                      >
                        {estadosFiltro.map((e) => (
                          <option key={e.value || "all"} value={e.value}>
                            {e.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={8}>
                      <Form.Label className="dh-form-label">Buscar</Form.Label>
                      <Form.Control
                        className="dh-form-control"
                        placeholder="Zona, módulo, código, id proveedor..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <div style={{ maxHeight: "520px", overflow: "auto" }}>
                    <Table striped bordered hover responsive className="table-homologacion mb-0">
                      <thead>
                        <tr>
                          {esSync && (
                            <th style={{ width: 42 }}>
                              <Form.Check
                                type="checkbox"
                                checked={todosSeleccionados}
                                onChange={toggleTodosVisibles}
                                disabled={!idsSeleccionables.length || ocupado}
                                title="Seleccionar visibles"
                              />
                            </th>
                          )}
                          <th>Estado</th>
                          <th>ID SGC</th>
                          <th>Zona</th>
                          <th>Módulo</th>
                          <th>Hab.</th>
                          <th>Cama</th>
                          <th>{esSync ? "Código SGC→LF" : "Código SGC"}</th>
                          <th>{esValidacion ? "ID proveedor (BD)" : "ID proveedor"}</th>
                          <th>{esSync ? "Perfil" : esValidacion ? "Cód. BD" : "Cód. proveedor"}</th>
                          {esValidacion && <th>Cód. Logiflex</th>}
                          {!esValidacion && !esSync && <th>Match</th>}
                          <th>Motivo</th>
                          {esSync && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filasFiltradas.length === 0 ? (
                          <tr>
                            <td
                              colSpan={esValidacion ? 11 : esSync ? 12 : 11}
                              className="text-center text-muted py-4"
                            >
                              Sin registros para el filtro seleccionado
                            </td>
                          </tr>
                        ) : (
                          filasFiltradas.map((f, idx) => (
                            <tr key={`${f.cama_id || "p"}-${f.id_proveedor || idx}`}>
                              {esSync && (
                                <td onClick={(e) => e.stopPropagation()}>
                                  {f.cama_id &&
                                  ["CREAR", "ACTUALIZAR", "YA_ALINEADA", "SIN_HABITACION"].includes(
                                    f.accion
                                  ) ? (
                                    <Form.Check
                                      type="checkbox"
                                      checked={seleccionados.includes(f.cama_id)}
                                      onChange={() => toggleFila(f.cama_id)}
                                      disabled={ocupado}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              )}
                              <td>
                                <Badge bg={badgeVariant(f.accion || f.estado)}>
                                  {f.accion || f.estado}
                                </Badge>
                              </td>
                              <td>{f.cama_id ?? "—"}</td>
                              <td>{f.zona ?? "—"}</td>
                              <td>{f.modulo ?? "—"}</td>
                              <td>{f.habitacion ?? "—"}</td>
                              <td>{f.cama ?? "—"}</td>
                              <td>
                                <small>{f.codigo_sgc ?? f.codigo_esperado ?? "—"}</small>
                              </td>
                              <td>
                                <small>{f.id_proveedor ?? "—"}</small>
                                {!esValidacion &&
                                  !esSync &&
                                  f.id_proveedor_anterior &&
                                  f.id_proveedor_anterior !== f.id_proveedor && (
                                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                      en BD: {f.id_proveedor_anterior}
                                    </div>
                                  )}
                              </td>
                              <td>
                                <small>
                                  {esSync
                                    ? f.perfil ?? "—"
                                    : f.codigo_proveedor ?? "—"}
                                </small>
                              </td>
                              {esValidacion && (
                                <td>
                                  <small>{f.codigo_logiflex ?? "—"}</small>
                                </td>
                              )}
                              {!esValidacion && !esSync && <td>{f.metodo_match ?? "—"}</td>}
                              <td>
                                <small className="text-muted">{f.motivo ?? ""}</small>
                              </td>
                              {esSync && (
                                <td>
                                  {f.cama_id &&
                                  (f.accion === "CREAR" ||
                                    f.accion === "ACTUALIZAR" ||
                                    f.accion === "SIN_HABITACION") ? (
                                    <Button
                                      size="sm"
                                      className="btn-dh-secondary"
                                      disabled={ocupado || syncUnaId === f.cama_id}
                                      onClick={() => sincronizarUna(f.cama_id)}
                                    >
                                      {syncUnaId === f.cama_id ? (
                                        <Spinner size="sm" />
                                      ) : f.accion === "CREAR" ? (
                                        "Crear"
                                      ) : f.accion === "ACTUALIZAR" ? (
                                        "Actualizar"
                                      ) : (
                                        "Reintentar"
                                      )}
                                    </Button>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default HomologacionCamasLogiflex;
